import { useState, useCallback } from 'react';
import { KeyRound, CheckCircle, XCircle, Shield, Calendar, Fingerprint, Server, Lock, ShieldCheck } from 'lucide-react';
import * as forge from 'node-forge';

interface CertificateDetails {
  subject: {
    CN?: string;
    O?: string;
    OU?: string;
    C?: string;
    ST?: string;
    L?: string;
  };
  issuer: {
    CN?: string;
    O?: string;
    OU?: string;
    C?: string;
  };
  validFrom: string;
  validTo: string;
  daysRemaining: number;
  serialNumber: string;
  fingerprint: string;
  fingerprint256: string;
  subjectAltNames: string[];
  keyUsage: string[];
  extKeyUsage: string[];
  isCA: boolean;
  isSelfSigned: boolean;
  version: number;
  signatureAlgorithm: string;
  publicKeyAlgorithm: string;
  publicKeySize: number | null;
}

interface MatchResult {
  match: boolean;
  keyType: string;
  certificate: CertificateDetails;
  timestamp: string;
}

// Helper to extract attribute from certificate name
function getAttr(name: forge.pki.CertificateField[], shortName: string): string | undefined {
  const attr = name.find(a => a.shortName === shortName);
  return attr?.value as string | undefined;
}

// Parse Subject Alt Names from certificate
function getSubjectAltNames(cert: forge.pki.Certificate): string[] {
  const sans: string[] = [];
  const ext = cert.getExtension('subjectAltName');
  if (ext && (ext as { altNames?: Array<{ type: number; value: string }> }).altNames) {
    for (const alt of (ext as { altNames: Array<{ type: number; value: string }> }).altNames) {
      if (alt.type === 2) { // DNS
        sans.push(alt.value);
      } else if (alt.type === 7) { // IP
        sans.push(alt.value);
      }
    }
  }
  return sans;
}

// Parse Key Usage from certificate
function getKeyUsage(cert: forge.pki.Certificate): string[] {
  const usages: string[] = [];
  const ext = cert.getExtension('keyUsage') as { digitalSignature?: boolean; nonRepudiation?: boolean; keyEncipherment?: boolean; dataEncipherment?: boolean; keyAgreement?: boolean; keyCertSign?: boolean; cRLSign?: boolean; encipherOnly?: boolean; decipherOnly?: boolean } | null;
  if (ext) {
    if (ext.digitalSignature) usages.push('Digital Signature');
    if (ext.nonRepudiation) usages.push('Non Repudiation');
    if (ext.keyEncipherment) usages.push('Key Encipherment');
    if (ext.dataEncipherment) usages.push('Data Encipherment');
    if (ext.keyAgreement) usages.push('Key Agreement');
    if (ext.keyCertSign) usages.push('Certificate Sign');
    if (ext.cRLSign) usages.push('CRL Sign');
  }
  return usages;
}

// Parse Extended Key Usage from certificate  
function getExtKeyUsage(cert: forge.pki.Certificate): string[] {
  const usages: string[] = [];
  const ext = cert.getExtension('extKeyUsage') as { serverAuth?: boolean; clientAuth?: boolean; codeSigning?: boolean; emailProtection?: boolean; timeStamping?: boolean } | null;
  if (ext) {
    if (ext.serverAuth) usages.push('TLS Web Server Authentication');
    if (ext.clientAuth) usages.push('TLS Web Client Authentication');
    if (ext.codeSigning) usages.push('Code Signing');
    if (ext.emailProtection) usages.push('E-mail Protection');
    if (ext.timeStamping) usages.push('Time Stamping');
  }
  return usages;
}

// Check if certificate is CA
function isCA(cert: forge.pki.Certificate): boolean {
  const ext = cert.getExtension('basicConstraints') as { cA?: boolean } | null;
  return ext?.cA === true;
}

// Get fingerprint in formatted hex
function getFingerprint(cert: forge.pki.Certificate, algorithm: 'sha1' | 'sha256'): string {
  const der = forge.asn1.toDer(forge.pki.certificateToAsn1(cert)).getBytes();
  const md = algorithm === 'sha1' ? forge.md.sha1.create() : forge.md.sha256.create();
  md.update(der);
  const hex = md.digest().toHex().toUpperCase();
  return hex.match(/.{2}/g)?.join(':') || hex;
}

// Get public key size
function getPublicKeySize(publicKey: forge.pki.PublicKey): number | null {
  if ('n' in publicKey) {
    // RSA key - n is the modulus
    return (publicKey as forge.pki.rsa.PublicKey).n.bitLength();
  }
  return null;
}

// Get signature algorithm name
function getSignatureAlgorithm(cert: forge.pki.Certificate): string {
  const oid = cert.siginfo.algorithmOid;
  const oidMap: Record<string, string> = {
    '1.2.840.113549.1.1.5': 'SHA-1 with RSA',
    '1.2.840.113549.1.1.11': 'SHA-256 with RSA',
    '1.2.840.113549.1.1.12': 'SHA-384 with RSA',
    '1.2.840.113549.1.1.13': 'SHA-512 with RSA',
    '1.2.840.10045.4.3.2': 'ECDSA with SHA-256',
    '1.2.840.10045.4.3.3': 'ECDSA with SHA-384',
    '1.2.840.10045.4.3.4': 'ECDSA with SHA-512',
  };
  return oidMap[oid] || `OID: ${oid}`;
}

// Parse certificate and extract all details
function parseCertificate(pem: string): { cert: forge.pki.Certificate; details: CertificateDetails } {
  const cert = forge.pki.certificateFromPem(pem);
  
  const validFrom = cert.validity.notBefore;
  const validTo = cert.validity.notAfter;
  const now = new Date();
  const daysRemaining = Math.floor((validTo.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  const isSelfSigned = cert.subject.hash === cert.issuer.hash;
  
  const details: CertificateDetails = {
    subject: {
      CN: getAttr(cert.subject.attributes, 'CN'),
      O: getAttr(cert.subject.attributes, 'O'),
      OU: getAttr(cert.subject.attributes, 'OU'),
      C: getAttr(cert.subject.attributes, 'C'),
      ST: getAttr(cert.subject.attributes, 'ST'),
      L: getAttr(cert.subject.attributes, 'L'),
    },
    issuer: {
      CN: getAttr(cert.issuer.attributes, 'CN'),
      O: getAttr(cert.issuer.attributes, 'O'),
      OU: getAttr(cert.issuer.attributes, 'OU'),
      C: getAttr(cert.issuer.attributes, 'C'),
    },
    validFrom: validFrom.toISOString(),
    validTo: validTo.toISOString(),
    daysRemaining,
    serialNumber: cert.serialNumber.toUpperCase(),
    fingerprint: getFingerprint(cert, 'sha1'),
    fingerprint256: getFingerprint(cert, 'sha256'),
    subjectAltNames: getSubjectAltNames(cert),
    keyUsage: getKeyUsage(cert),
    extKeyUsage: getExtKeyUsage(cert),
    isCA: isCA(cert),
    isSelfSigned,
    version: cert.version + 1, // forge uses 0-indexed version
    signatureAlgorithm: getSignatureAlgorithm(cert),
    publicKeyAlgorithm: 'n' in cert.publicKey ? 'RSA' : 'EC',
    publicKeySize: getPublicKeySize(cert.publicKey),
  };
  
  return { cert, details };
}

// Parse private key from PEM (supports various formats)
function parsePrivateKey(pem: string): forge.pki.PrivateKey {
  // Try different parsers
  const trimmed = pem.trim();
  
  // Check for encrypted key
  if (trimmed.includes('ENCRYPTED')) {
    throw new Error('Encrypted private keys are not supported. Please decrypt the key first.');
  }
  
  // Try RSA private key
  if (trimmed.includes('RSA PRIVATE KEY')) {
    return forge.pki.privateKeyFromPem(pem);
  }
  
  // Try PKCS#8 format (BEGIN PRIVATE KEY)
  if (trimmed.includes('BEGIN PRIVATE KEY')) {
    return forge.pki.privateKeyFromPem(pem);
  }
  
  // Try EC private key - node-forge has limited EC support
  if (trimmed.includes('EC PRIVATE KEY')) {
    throw new Error('EC private keys require conversion. Use: openssl ec -in key.pem -outform PEM');
  }
  
  throw new Error('Unrecognized private key format');
}

// Compare public keys from certificate and private key
function keysMatch(cert: forge.pki.Certificate, privateKey: forge.pki.PrivateKey): { match: boolean; keyType: string } {
  const certPubKey = cert.publicKey;
  
  // Check if both are RSA
  if ('n' in certPubKey && 'n' in privateKey) {
    const certRsa = certPubKey as forge.pki.rsa.PublicKey;
    const keyRsa = privateKey as forge.pki.rsa.PrivateKey;
    
    // Compare modulus (n) and public exponent (e)
    const modulusMatch = certRsa.n.compareTo(keyRsa.n) === 0;
    const exponentMatch = certRsa.e.compareTo(keyRsa.e) === 0;
    
    const keySize = keyRsa.n.bitLength();
    return {
      match: modulusMatch && exponentMatch,
      keyType: `RSA ${keySize}-bit`,
    };
  }
  
  return {
    match: false,
    keyType: 'Unknown key type or type mismatch',
  };
}

export function CertMatcher() {
  const [certificate, setCertificate] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [result, setResult] = useState<MatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleMatch = useCallback(() => {
    if (!certificate.trim()) {
      setError('Please enter a certificate');
      return;
    }

    if (!privateKey.trim()) {
      setError('Please enter a private key');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    // Use setTimeout to allow UI to update before potentially heavy computation
    setTimeout(() => {
      try {
        // Parse certificate
        const { cert, details } = parseCertificate(certificate.trim());
        
        // Parse private key
        const privKey = parsePrivateKey(privateKey.trim());
        
        // Compare keys
        const { match, keyType } = keysMatch(cert, privKey);
        
        setResult({
          match,
          keyType,
          certificate: details,
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to parse certificate or key';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    }, 10);
  }, [certificate, privateKey]);

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (days: number) => {
    if (days <= 0) return 'text-red-500 dark:text-red-400';
    if (days <= 30) return 'text-red-500 dark:text-red-400';
    return 'text-[var(--accent)]';
  };

  const getDaysLabel = (days: number) => {
    if (days <= 0) return 'Expired';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  };

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (!result) {
      return (
        <div className="text-[var(--text-muted)] text-center py-8">
          Enter a certificate and private key to check if they match. The certificate details will also be decoded and displayed.
        </div>
      );
    }

    const cert = result.certificate;

    return (
      <div className="space-y-6">
        {/* Match Status */}
        <div className={`flex items-center gap-4 p-4 rounded-lg ${result.match ? 'bg-[var(--accent-dim)]' : 'bg-red-500/10'}`}>
          {result.match ? (
            <CheckCircle className="w-10 h-10 text-[var(--accent)]" />
          ) : (
            <XCircle className="w-10 h-10 text-red-400" />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className={`font-semibold text-lg ${result.match ? 'text-[var(--accent)]' : 'text-red-400'}`}>
                {result.match ? 'Certificate and Private Key Match!' : 'Certificate and Private Key Do NOT Match'}
              </span>
            </div>
            <div className="text-sm text-[var(--text-muted)] mt-1 flex items-center gap-2">
              <Lock className="w-3 h-3" />
              {result.keyType}
            </div>
          </div>
        </div>

        {/* Certificate Details */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Certificate Details
          </h3>
          <div className="space-y-4">
            {/* Subject */}
            <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
              <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-2">
                <Server className="w-4 h-4" />
                Subject
              </h4>
              <div className="space-y-1 text-sm">
                {cert.subject.CN && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-[var(--text-muted)]">Common Name:</span>
                    <span className="font-medium break-all">{cert.subject.CN}</span>
                  </div>
                )}
                {cert.subject.O && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-[var(--text-muted)]">Organization:</span>
                    <span>{cert.subject.O}</span>
                  </div>
                )}
                {cert.subject.OU && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-[var(--text-muted)]">Org Unit:</span>
                    <span>{cert.subject.OU}</span>
                  </div>
                )}
                {(cert.subject.L || cert.subject.ST || cert.subject.C) && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-[var(--text-muted)]">Location:</span>
                    <span>{[cert.subject.L, cert.subject.ST, cert.subject.C].filter(Boolean).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Issuer */}
            <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
              <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">Issuer</h4>
              <div className="space-y-1 text-sm">
                {cert.issuer.CN && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-[var(--text-muted)]">Common Name:</span>
                    <span className="font-medium break-all">{cert.issuer.CN}</span>
                  </div>
                )}
                {cert.issuer.O && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-[var(--text-muted)]">Organization:</span>
                    <span>{cert.issuer.O}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Validity */}
            <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
              <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Validity
              </h4>
              <div className="space-y-1 text-sm">
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <span className="text-[var(--text-muted)]">Not Before:</span>
                  <span>{formatDate(cert.validFrom)}</span>
                </div>
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <span className="text-[var(--text-muted)]">Not After:</span>
                  <span className="flex items-center gap-2">
                    {formatDate(cert.validTo)}
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(cert.daysRemaining)} bg-[var(--bg-tertiary)]`}>
                      {getDaysLabel(cert.daysRemaining)}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Technical Details */}
            <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
              <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-2">
                <Fingerprint className="w-4 h-4" />
                Technical Details
              </h4>
              <div className="space-y-1 text-sm">
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-[var(--text-muted)]">Serial Number:</span>
                  <span className="font-mono text-xs break-all">{cert.serialNumber}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-[var(--text-muted)]">Signature Alg:</span>
                  <span>{cert.signatureAlgorithm}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-[var(--text-muted)]">Public Key:</span>
                  <span>{cert.publicKeyAlgorithm}{cert.publicKeySize ? ` (${cert.publicKeySize} bit)` : ''}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-[var(--text-muted)]">Is CA:</span>
                  <span>{cert.isCA ? 'Yes' : 'No'}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-[var(--text-muted)]">SHA-1:</span>
                  <span className="font-mono text-xs break-all">{cert.fingerprint}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-[var(--text-muted)]">SHA-256:</span>
                  <span className="font-mono text-xs break-all">{cert.fingerprint256}</span>
                </div>
              </div>
            </div>

            {/* Subject Alt Names */}
            {cert.subjectAltNames.length > 0 && (
              <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
                <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">
                  Subject Alternative Names ({cert.subjectAltNames.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {cert.subjectAltNames.slice(0, 15).map((san, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-xs font-mono"
                    >
                      {san}
                    </span>
                  ))}
                  {cert.subjectAltNames.length > 15 && (
                    <span className="px-2 py-1 text-xs text-[var(--text-muted)]">
                      +{cert.subjectAltNames.length - 15} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Client-side badge */}
      <div className="flex items-center gap-2 text-xs text-[var(--accent)]">
        <ShieldCheck className="w-4 h-4" />
        <span>100% Client-Side — Your keys never leave your browser</span>
      </div>

      {/* Input Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--text-primary)]">
            Certificate (PEM)
          </label>
          <textarea
            className="w-full h-48 px-4 py-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-xs resize-none"
            placeholder="-----BEGIN CERTIFICATE-----
MIIDXTCCAkWgAwIBAgIJAJC1HiIAZAiUMA...
-----END CERTIFICATE-----"
            value={certificate}
            onChange={(e) => setCertificate(e.target.value)}
            spellCheck={false}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--text-primary)]">
            Private Key (PEM)
          </label>
          <textarea
            className="w-full h-48 px-4 py-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-xs resize-none"
            placeholder="-----BEGIN PRIVATE KEY-----
MIIEvgIBADANBgkqhkiG9w0BAQEFAASC...
-----END PRIVATE KEY-----"
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            spellCheck={false}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={handleMatch}
        disabled={isLoading || !certificate.trim() || !privateKey.trim()}
        className="btn-primary flex items-center gap-2 px-6"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Checking...
          </>
        ) : (
          <>
            <KeyRound className="w-4 h-4" />
            Check Match & Decode
          </>
        )}
      </button>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          Results
        </label>
        <div className="output-panel">
          {renderOutput()}
        </div>
      </div>

      <div className="text-xs text-[var(--text-muted)]">
        <span className="text-[var(--accent)]">Note:</span> This tool compares the public key in the certificate with the public key derived from the private key.
        All processing happens in your browser — RSA keys are fully supported.
      </div>
    </div>
  );
}
